import React from 'react';
import { Layers, Palette } from 'lucide-react';

interface BadgeCustomizerControlsProps {
  badgeStyle: 'pill' | 'card' | 'shield' | 'button';
  theme: 'emerald' | 'light' | 'dark' | 'indigo';
  onBadgeStyleChange: (style: 'pill' | 'card' | 'shield' | 'button') => void;
  onThemeChange: (theme: 'emerald' | 'light' | 'dark' | 'indigo') => void;
}

export const BadgeCustomizerControls: React.FC<BadgeCustomizerControlsProps> = ({
  badgeStyle,
  theme,
  onBadgeStyleChange,
  onThemeChange,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
      {/* Style Selector */}
      <div>
        <label className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-1.5 mb-2">
          <Layers className="w-3.5 h-3.5 text-indigo-600" />
          Gaya / Format Lencana
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onBadgeStyleChange('pill')}
            className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
              badgeStyle === 'pill'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <div className="font-bold text-xs text-slate-900">Badge Kapsul (Pill)</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Ramping, memuat teks resmi & kode UIBN</div>
          </button>

          <button
            type="button"
            onClick={() => onBadgeStyleChange('card')}
            className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
              badgeStyle === 'card'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <div className="font-bold text-xs text-slate-900">Kartu Lengkap (Card)</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Judul, penerbit, nomor & tombol aksi</div>
          </button>

          <button
            type="button"
            onClick={() => onBadgeStyleChange('shield')}
            className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
              badgeStyle === 'shield'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <div className="font-bold text-xs text-slate-900">Segel Perisai (Shield)</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Format standar UIBN | Terdaftar</div>
          </button>

          <button
            type="button"
            onClick={() => onBadgeStyleChange('button')}
            className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
              badgeStyle === 'button'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <div className="font-bold text-xs text-slate-900">Tombol Aksi (Button)</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Tombol klik verifikasi interaktif</div>
          </button>
        </div>
      </div>

      {/* Theme Selector */}
      <div>
        <label className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-1.5 mb-2">
          <Palette className="w-3.5 h-3.5 text-indigo-600" />
          Pilihan Tema Warna
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onThemeChange('emerald')}
            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2.5 cursor-pointer ${
              theme === 'emerald'
                ? 'bg-emerald-50/90 border-emerald-600 ring-2 ring-emerald-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-emerald-600 shrink-0 border border-white shadow-xs"></span>
            <div>
              <div className="font-bold text-xs text-slate-900">Hijau Resmi</div>
              <div className="text-[10px] text-slate-500">Emerald Verified</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onThemeChange('light')}
            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2.5 cursor-pointer ${
              theme === 'light'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white shrink-0 border-2 border-slate-300 shadow-xs"></span>
            <div>
              <div className="font-bold text-xs text-slate-900">Putih Bersih</div>
              <div className="text-[10px] text-slate-500">Light Minimalist</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onThemeChange('dark')}
            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2.5 cursor-pointer ${
              theme === 'dark'
                ? 'bg-slate-900 text-white border-slate-900 ring-2 ring-slate-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-slate-900 shrink-0 border border-slate-700 shadow-xs"></span>
            <div>
              <div className={`font-bold text-xs ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                Gelap Modern
              </div>
              <div className={`text-[10px] ${theme === 'dark' ? 'text-slate-300' : 'text-slate-500'}`}>
                Dark Theme
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onThemeChange('indigo')}
            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2.5 cursor-pointer ${
              theme === 'indigo'
                ? 'bg-indigo-50/90 border-indigo-600 ring-2 ring-indigo-400/40'
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-indigo-700 shrink-0 border border-white shadow-xs"></span>
            <div>
              <div className="font-bold text-xs text-slate-900">Indigo UIBN</div>
              <div className="text-[10px] text-slate-500">Deep Indigo</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
